/**
 * useMeetingVisibilityActions — the single gate for the restrict/share
 * actions on a meeting's access badge.
 *
 * Both meeting surfaces (the slideout's MeetingSubHeader and the full-page
 * record header) render the same EmailThreadVisibilityBadge; this hook keeps
 * the decision of WHEN the actions appear in one place so the two never
 * drift. Actions render only when:
 *   - the `meeting_privacy_controls` flag is on for the org,
 *   - the object is an ahoy_meeting with an access_summary, and
 *   - the viewer is an attendee (viewer_is_owner) or a permissions admin.
 * The server 403 remains the real authorization backstop.
 *
 * Returns the memoized element (or undefined) to pass as
 * `visibilityActions` — a stable reference across renders.
 */

import { createElement, type ReactNode, useMemo } from 'react';

import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { usePermissions } from '@/hooks/usePermissions';
import type { CrmObject } from '@/types/crm';

import { MeetingVisibilityActions } from '../../components/MeetingVisibilityActions';

const MEETING_PRIVACY_FLAG = 'meeting_privacy_controls';
const PERMISSIONS_ADMIN_GROUP = 'ahoy_permissions_admin';

export function useMeetingVisibilityActions(
  object: CrmObject
): ReactNode | undefined {
  const flagOn = useFeatureFlag(MEETING_PRIVACY_FLAG);
  const { hasPermissionGroup } = usePermissions();
  const summary = object.access_summary;
  const canControl =
    flagOn &&
    object.entity_type_name === 'ahoy_meeting' &&
    !!summary &&
    (summary.viewer_is_owner || hasPermissionGroup(PERMISSIONS_ADMIN_GROUP));

  return useMemo(
    () =>
      canControl && summary
        ? createElement(MeetingVisibilityActions, {
            orgId: object.org_id,
            meetingEntityId: object.id,
            privacyState: summary.privacy_state,
          })
        : undefined,
    [canControl, summary, object.org_id, object.id]
  );
}
